import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { BackButton } from "@/components/BackButton";
import { FloatingThemeToggle } from "@/context/ThemeContext";

export default function ForgotPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState(params.get("email") || "");
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); setError(""); try { await fn(); } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); } setBusy(false); };
  const request = (e) => { e.preventDefault(); run(async () => { await api.post("/auth/forgot-password", { email }); setSent(true); }); };
  const reset = (e) => {
    e.preventDefault();
    if (pw.length < 8) return setError("Choose a password with at least 8 characters.");
    if (pw !== pw2) return setError("The two passwords don't match.");
    return run(async () => {
      await api.post("/auth/reset-password", { email, code, new_password: pw });
      toast.success("Password changed — log in with your new password");
      navigate(`/login`);
    });
  };
  const field = "mt-1 border-border bg-background";
  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary px-4">
      <FloatingThemeToggle />
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-xl shadow-amber-900/5" data-testid="forgot-password-page">
        <BackButton testId="forgot-back-button" />
        <Link to="/" className="mt-2 flex items-center justify-center"><Logo height={54} className="dark:shadow-sm" /></Link>
        <h1 className="mt-6 text-center font-serif text-3xl text-foreground">Reset your password</h1>
        <p className="mt-2 text-center text-sm text-muted-foreground" data-testid="forgot-intro">
          {sent ? "If that email has an account, we've sent a 6-digit code. It expires in 15 minutes." : "Enter your email and we'll send you a 6-digit code."}
        </p>
        {!sent ? (
          <form onSubmit={request} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="fp-email" className="text-muted-foreground">Email</Label>
              <Input id="fp-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" placeholder="you@example.com" className={field} />
            </div>
            {error && <p data-testid="forgot-error" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
            <Button type="submit" disabled={busy} data-testid="forgot-send" className="w-full rounded-full bg-primary py-6 text-primary-foreground hover:bg-primary/90">{busy ? "Sending…" : "Send me a code"}</Button>
          </form>
        ) : (
          <form onSubmit={reset} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="fp-code" className="text-muted-foreground">6-digit code</Label>
              <Input id="fp-code" inputMode="numeric" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="forgot-code" placeholder="123456" className={`${field} font-mono tracking-[0.4em]`} />
            </div>
            <div>
              <Label htmlFor="fp-pw" className="text-muted-foreground">New password</Label>
              <Input id="fp-pw" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="forgot-new-password" placeholder="At least 8 characters" className={field} />
            </div>
            <div>
              <Label htmlFor="fp-pw2" className="text-muted-foreground">Confirm new password</Label>
              <Input id="fp-pw2" type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="forgot-confirm-password" className={field} />
            </div>
            {error && <p data-testid="forgot-error" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
            <Button type="submit" disabled={busy || code.length !== 6} data-testid="forgot-reset" className="w-full rounded-full bg-primary py-6 text-primary-foreground hover:bg-primary/90">{busy ? "Saving…" : "Set new password"}</Button>
            <button type="button" onClick={() => { setSent(false); setCode(""); }} data-testid="forgot-resend" className="w-full text-center text-xs text-primary hover:underline">Didn't get it? Send a new code</button>
          </form>
        )}
        <p className="mt-6 text-center text-sm text-muted-foreground">Remembered it? <Link to="/login" data-testid="forgot-to-login" className="font-medium text-primary hover:underline">Log in</Link></p>
      </div>
    </div>
  );
}
